import { FunctionComponent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from './redux/hooks';
import {
  setIsRejectedDocument,
  setRefetchGetUser,
  setProfile
} from './redux/slices/user/profileSlice';
import { GET_ONBOARDING_PRESETS, GET_USER } from './graphql/queries';
import useCustomQuery from './network/useCustomQuery';
import { setOnboardingPresets } from './redux/slices/onboarding/onboardingSlice';
import SuspenseLoader from './components/Loader/SuspenseLoader';
import { shouldGetProfileCheck } from './util/helpers/routeHelpers';

export interface IAuthComp {
  children?: any;
}

const AuthGuard: FunctionComponent<IAuthComp> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { refetchGetUser, profile } = useSelector((state: any) => state?.userProfile);
  const { onboardingPresets } = useSelector((state: any) => state?.onboarding);

  const handleError = () => {
    navigate('/');

    // To clear the current profile and its associated Redux store.
    window.location.reload();
  };

  const [getProfileQuery, profileLoading, profileData] = useCustomQuery({
    query: GET_USER,
    onError: (err: any) => {
      if (err) {
        if (
          err?.message?.toLowerCase?.() === 'sorry you have no access to this resource.' ||
          err?.message?.toLowerCase?.() === 'user not found'
        ) {
          handleError();
        }
      }
    },
    onData: () => {}
  });

  const [getOnboardingPresets, , getOnboardingPresetsData] = useCustomQuery({
    query: GET_ONBOARDING_PRESETS,
    onError: () => {},
    onData: () => {
      dispatch(setOnboardingPresets(getOnboardingPresetsData?.getOnboardingPresets?.data));
    }
  });

  const shouldGetProfile = shouldGetProfileCheck(
    [
      '/',
      '/create-tenant',
      '/otp-login',
      '/get-started',
      '/payment-success',
      '/forgot-password',
      '/get-verified',
      '/forgot-password-verify',
      '/password-reset',
      '/pay/rent/*'
    ],
    pathname
  );

  const documentStatus = (data: any) => {
    const documents = data?.documents;
    const findRejectedFile = documents?.find((list: any) => list?.status === '-1');
    if (findRejectedFile) {
      dispatch(setIsRejectedDocument(true));
      return;
    }
    dispatch(setIsRejectedDocument(false));
  };

  useEffect(() => {
    if (profileData?.getUserProfileCa) {
      dispatch(setProfile(profileData?.getUserProfileCa?.data));
      documentStatus(profileData?.getUserProfileCa?.data);
      if (!onboardingPresets?.enrollmentTypes?.length) {
        getOnboardingPresets();
      }
    }
  }, [profileData]);

  // TODO: Change the below to Saga or RTK Listener.
  useEffect(() => {
    if ((shouldGetProfile && localStorage?.token) || refetchGetUser) {
      getProfileQuery();
    }
    return () => {
      dispatch(setRefetchGetUser(false));
    };
  }, [shouldGetProfile, localStorage?.token, refetchGetUser]);

  useEffect(() => {
    if (!localStorage?.token && shouldGetProfile) {
      handleError();
    }
  }, [shouldGetProfile, localStorage?.token, pathname, profileLoading]);

  if (profileLoading && !profile) {
    return <SuspenseLoader />;
  }

  // TODO: Add a fallback component when getting the profile fails.
  // if (!profileLoading && !profile) {
  //   return <div>something is up</div>;
  // }
  return children;
};

export default AuthGuard;
